import { type ReactNode, createContext, useContext } from "react";

/**
 * Lets the UI switch the active affiliate (tenant).
 *
 * Provided by `useAuthStoreSync`'s bridge at the app root and consumed by the
 * sidebar switcher. Kept in its own context - rather than on the router
 * `AuthState` - so the read-only auth state stays a plain serialisable value.
 */
export interface AffiliateSelection {
  /** The effective affiliate id, or `null` when none is known. */
  readonly affiliateId: string | null;
  /** Every affiliate the organization claim allows, in claim order. */
  readonly availableAffiliateIds: readonly string[];
  /**
   * Whether a switcher should be offered at all: only when the claim carries
   * more than one organization, matching admin v1.
   */
  readonly canSwitch: boolean;
  /** Switches the active affiliate. Ignores ids outside the claim. */
  readonly selectAffiliate: (affiliateId: string) => void;
}

const AffiliateSelectionContext = createContext<AffiliateSelection | null>(
  null,
);

/**
 * Reads the affiliate selection. Throws outside the provider so a consumer never
 * silently renders a switcher that cannot switch.
 */
export function useAffiliateSelection(): AffiliateSelection {
  const selection = useContext(AffiliateSelectionContext);
  if (selection === null) {
    throw new Error(
      "useAffiliateSelection must be used within an AffiliateSelectionProvider.",
    );
  }
  return selection;
}

export function AffiliateSelectionProvider({
  value,
  children,
}: {
  value: AffiliateSelection;
  children: ReactNode;
}): ReactNode {
  return (
    <AffiliateSelectionContext.Provider value={value}>
      {children}
    </AffiliateSelectionContext.Provider>
  );
}
